import {
    VStack,
    Button,
    HStack,
    useToast,
    Heading,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    Select,
    Switch,
    FormControl,
    FormLabel,
    Box,
    Flex,
    Icon,
    Divider,
    useColorModeValue,
    Container,
    IconButton,
    Input
} from "@chakra-ui/react";
import React, { useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { FiLogOut, FiSettings, FiVolume2, FiServer, FiUser, FiChevronRight, FiCopy, FiLock } from "react-icons/fi";
import { Helmet } from "react-helmet";
import { DefaultServer, LogoutApi, ChangePasswordApi } from "../../utils/api";

const Setting = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { Token, setIsAuth, defaultServer, setDefaultServer, userEmail, otpSound, setOtpSound } = useContext(AppContext);
    const toast = useToast();

    // Premium UI Colors
    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const subTextColor = useColorModeValue("gray.500", "gray.400");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const iconBg = useColorModeValue("brand.50", "brand.900");
    const iconColor = useColorModeValue("brand.500", "brand.200");

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const handlePasswordUpdate = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast({
                title: "All fields are required",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            toast({
                title: "Passwords do not match",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (newPassword.length < 6) {
            toast({
                title: "Password must be at least 6 characters",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);
        try {
       
            await ChangePasswordApi({
                oldPassword: currentPassword,
                newPassword: newPassword,
            }, Token);

            toast({
                title: "Password updated successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error) {
            toast({
                title: error.response?.data?.message || "Error updating password",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        LogoutApi(Token).then((res) => {
            onClose();
            localStorage.removeItem("token");
            setIsAuth(false);
            toast({
                status: "success",
                title: "Successfully logout",
                isClosable: true,
                duration: 2000,
            });
        }).catch((err) => {
            toast({
                title: err.response?.data?.message || 'Error contact admin',
                status: "error",
                isClosable: false,
            });
        })
    };

    const handleDefaultServer = (event) => {
        setDefaultServer(event.target.value)
        DefaultServer(Token, event.target.value).then((res) => {
            toast({
                status: 'success',
                title: res.data.message,
                isClosable: true
            })
        }).catch((err) => {
            console.log(err, 'chekcing error');
            toast({
                title: err.response?.data?.message || 'Error contact admin',
                status: "error",
                isClosable: false,
            });
        })
    }

    return (
        <>
            <Helmet>
                <title>Settings | Techy India</title>
            </Helmet>
            <VStack
                w="100%"
                minH="100vh"
                bg={bg}
                spacing={0}
                align="stretch"
                py={8}
            >
                <Container maxW="container.md">
                    <VStack spacing={8} align="stretch">
                        <VStack spacing={2} align="start">
                            <Heading size="lg" color={textColor}>Settings</Heading>
                            <Text color={subTextColor}>Manage your account preferences and settings</Text>
                        </VStack>

                        {/* Account Section */}
                        <Box
                            bg={cardBg}
                            rounded="2xl"
                            shadow="sm"
                            border="1px solid"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Account</Text>
                            </Box>
                            <VStack spacing={0} align="stretch">
                                <Flex
                                    p={6}
                                    align="center"
                                    justify="space-between"
                                    _hover={{ bg: hoverBg }}
                                >
                                    <HStack spacing={4}>
                                        <Flex
                                            align="center"
                                            justify="center"
                                            bg={iconBg}
                                            p={3}
                                            rounded="full"
                                            color={iconColor}
                                        >
                                            <Icon as={FiUser} boxSize={5} />
                                        </Flex>
                                        <VStack align="start" spacing={0}>
                                            <Text fontWeight="600" color={textColor}>Email Address</Text>
                                            <Text fontSize="sm" color={subTextColor}>{userEmail}</Text>
                                        </VStack>
                                    </HStack>
                                    <IconButton
                                        aria-label="Copy email"
                                        icon={<FiCopy />}
                                        size="sm"
                                        variant="ghost"
                                        color={subTextColor}
                                        onClick={() => {
                                            navigator.clipboard.writeText(userEmail);
                                            toast({
                                                title: "Email Copied",
                                                status: "success",
                                                duration: 2000,
                                                isClosable: true,
                                            });
                                        }}
                                        _hover={{ bg: useColorModeValue("gray.100", "whiteAlpha.200"), color: iconColor }}
                                    />
                                </Flex>
                            </VStack>
                        </Box>

                        {/* Change Password Section */}
                        <Box
                            bg={cardBg}
                            rounded="2xl"
                            shadow="sm"
                            border="1px solid"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Change Password</Text>
                            </Box>
                            <VStack spacing={4} p={6} align="stretch">
                                <FormControl>
                                    <FormLabel color={subTextColor}>Current Password</FormLabel>
                                    <Input
                                        type="text"
                                        placeholder="Enter current password"
                                        bg={useColorModeValue("gray.50", "gray.700")}
                                        border="1px solid"
                                        borderColor={borderColor}
                                        _focus={{ borderColor: "brand.500", boxShadow: "none" }}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel color={subTextColor}>New Password</FormLabel>
                                    <Input
                                        type="text"
                                        placeholder="Enter new password"
                                        bg={useColorModeValue("gray.50", "gray.700")}
                                        border="1px solid"
                                        borderColor={borderColor}
                                        _focus={{ borderColor: "brand.500", boxShadow: "none" }}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel color={subTextColor}>Confirm New Password</FormLabel>
                                    <Input
                                        type="text"
                                        placeholder="Confirm new password"
                                        bg={useColorModeValue("gray.50", "gray.700")}
                                        border="1px solid"
                                        borderColor={borderColor}
                                        _focus={{ borderColor: "brand.500", boxShadow: "none" }}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </FormControl>
                                <Button
                                    colorScheme="brand"
                                    size="md"
                                    w="full"
                                    mt={2}
                                    leftIcon={<FiLock />}
                                    onClick={handlePasswordUpdate}
                                    isLoading={loading}
                                    loadingText="Updating..."
                                >
                                    Update Password
                                </Button>
                            </VStack>
                        </Box>

                        {/* Preferences Section */}
                        <Box
                            bg={cardBg}
                            rounded="2xl"
                            shadow="sm"
                            border="1px solid"
                            borderColor={borderColor}
                            overflow="hidden"
                        >
                            <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                                <Text fontWeight="bold" fontSize="lg" color={textColor}>Preferences</Text>
                            </Box>
                            <VStack spacing={0} align="stretch">
                                {/* Default Server */}
                                <Flex p={6} align="center" justify="space-between" _hover={{ bg: hoverBg }}>
                                    <HStack spacing={4}>
                                        <Flex
                                            align="center"
                                            justify="center"
                                            bg="blue.50"
                                            p={3}
                                            rounded="full"
                                            color="blue.500"
                                        >
                                            <Icon as={FiServer} boxSize={5} />
                                        </Flex>
                                        <VStack align="start" spacing={0}>
                                            <Text fontWeight="600" color={textColor}>Default Server</Text>
                                            <Text fontSize="sm" color={subTextColor}>Select your preferred server</Text>
                                        </VStack>
                                    </HStack>
                                    <Select
                                        w="150px"
                                        value={defaultServer}
                                        onChange={handleDefaultServer}
                                        variant="filled"
                                        size="sm"
                                        rounded="lg"
                                    >
                                        <option value="server1">Operator 1</option>
                                        <option value="server2">Operator 2</option>
                                        <option value="server3">Operator 3</option>
                                    </Select>
                                </Flex>

                                <Divider borderColor={borderColor} />

                            </VStack>
                        </Box>

                        {/* Logout Button */}
                        <Button
                            size="lg"
                            variant="outline"
                            colorScheme="red"
                            rightIcon={<FiLogOut />}
                            onClick={onOpen}
                            w="100%"
                            h="60px"
                            fontSize="lg"
                            _hover={{ bg: "red.50" }}
                        >
                            Log Out
                        </Button>
                    </VStack>
                </Container>
            </VStack>

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent rounded="2xl">
                    <ModalCloseButton />
                    <ModalBody p={8} textAlign="center">
                        <VStack spacing={6}>
                            <Flex
                                align="center"
                                justify="center"
                                bg="red.50"
                                p={4}
                                rounded="full"
                                color="red.500"
                            >
                                <Icon as={FiLogOut} boxSize={8} />
                            </Flex>
                            <Box>
                                <Heading size="md" mb={2}>Log Out?</Heading>
                                <Text color="gray.500">Are you sure you want to log out of your account?</Text>
                            </Box>
                            <HStack w="100%" spacing={4}>
                                <Button
                                    flex={1}
                                    variant="ghost"
                                    onClick={onClose}
                                    size="lg"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    flex={1}
                                    colorScheme="red"
                                    onClick={handleLogout}
                                    size="lg"
                                    shadow="lg"
                                >
                                    Log Out
                                </Button>
                            </HStack>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default Setting;
